import { useEffect, useState, useMemo, useCallback } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { getAllMoisture, deleteMoisture, printMoistureCorrection } from "../../services/MoistureCorrectionServices";
import { displayError } from "../../helpers";
import ListFormPageHeader from "../../components/ListFormPageHeader";
import { showMoistureLinkedDcPopup } from "./MoistureCorrectionDcPopup";
import { openPdfResponse } from "../../utils/openPdfInApp";

function MoistureCorrectionTable() {

    const InitialData = {
        total_pages: 0,
        records: [],
    };

    const [data, setData] = useState(InitialData);
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);

    const handleDcCellClick = useCallback((row) => {
        if ((row.delivery_challan_count || 0) > 0) {
            showMoistureLinkedDcPopup(row, cookies);
        }
    }, [cookies]);

    const columns = useMemo(
        () => [
            { fieldName: "id", headerName: "ROW_ACTIONS",sticky: "right" },
            { fieldName: "plant_name", headerName: 'Plant' },
            { fieldName: "mc_no", headerName: 'MC No.' },
            { fieldName: "mc_date_display", headerName: 'MC Date' },
            { fieldName: "mc_time", headerName: 'MC Time' },
            { fieldName: "status", headerName: 'Status' },
            {
                fieldName: "delivery_challan_label",
                headerName: 'DC',
                getCellStyle: (row) => ({
                    color: (row.delivery_challan_count || 0) > 0 ? '#2563eb' : '#64748b',
                    cursor: (row.delivery_challan_count || 0) > 0 ? 'pointer' : 'default',
                    textDecoration: (row.delivery_challan_count || 0) > 0 ? 'underline' : 'none',
                    fontWeight: 600,
                }),
                onCellClick: handleDcCellClick,
            },
            { fieldName: "moisture_msand", headerName: 'Moi_MSand' },
            { fieldName: "moisture_csand", headerName: 'Moi_CSand' },
            { fieldName: "moisture_rsand", headerName: 'Moi_RSand' },
            { fieldName: "moisture_12mm", headerName: 'Moi_12mm' },
            { fieldName: "moisture_20mm", headerName: 'Moi_20mm' },
            { fieldName: "absorption_msand", headerName: 'Abs_MSand' },
            { fieldName: "absorption_csand", headerName: 'Abs_CSand' },
            { fieldName: "absorption_rsand", headerName: 'Abs_RSand' },
            { fieldName: "absorption_12mm", headerName: 'Abs_12mm' },
            { fieldName: "absorption_20mm", headerName: 'Abs_20mm' }
        ],
        [handleDcCellClick]
    );

    const onEditMoisturecorr = (moistureId) => {
        navigate(`/MoistureCorrectionEdit/${moistureId}/`);
    };

    const onDeleteMoisturecorr = (moistureId, event) => {
        event.preventDefault();
        Swal.fire({
            title: 'Are you sure to Delete?',
            showCancelButton: true,
            confirmButtonText: `Yes`,
            cancelButtonText: `No`,
        }).then((result) => {
            if (result.isConfirmed) {
                setIsLoading(true);
                deleteMoisture(cookies, moistureId);
                Swal.fire('Deleted Successfully!', '', 'success');
                setRefreshKey(oldKey => oldKey + 1)
            } else {
                Swal.fire('Not Deleted', '', 'info')
            }
        });
    };

    const onPrintMoistureCorrection = (moistureId, event) => {
        event.preventDefault();
        setIsLoading(true);
        printMoistureCorrection(cookies, moistureId)
            .then((response) => {
                openPdfResponse(response, {
                    title: 'Moisture Correction Report',
                    downloadFileName: `moisture_correction_${moistureId}.pdf`,
                });
            })
            .catch(async (error) => {
                let message = error?.response?.data || error;
                if (error?.response?.data instanceof Blob) {
                    try {
                        const text = await error.response.data.text();
                        const parsed = JSON.parse(text);
                        message = parsed.detail || parsed.message || parsed || text;
                    } catch {
                        message = error.message || 'Print failed';
                    }
                }
                displayError(message, 'Print Failed');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns.filter(col => col.headerName !== 'ROW_ACTIONS').map(c => c.headerName),
            data.records
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of Moisture Correction',
            columns.filter(col => col.headerName !== 'ROW_ACTIONS').map(col => ({
                header: col.headerName,
                dataKey: col.fieldName
            })),
            data.records,
            'Moisture.pdf'
        );
    };

    useEffect(() => {
        setIsLoading(true);
        getAllMoisture(cookies)
            .then((moistureList) => {
                const tableData = (moistureList?.moisture_correction_list || []).map((mc) => {
                    const deliveryChallans = mc.delivery_challans || [];
                    const deliveryChallanCount = Number(
                        mc.delivery_challan_count ?? deliveryChallans.length ?? 0
                    );
                    const deliveryChallanLabel = deliveryChallanCount > 0
                        ? String(deliveryChallanCount)
                        : '-';

                    return {
                    id: mc.id,
                    plant_name: mc.plant_name,
                    mc_no: mc.mc_no ?? '',
                    mc_date_display: mc.mc_date_display || mc.mc_date || '',
                    mc_time: mc.mc_time ? String(mc.mc_time).slice(0, 8) : '',
                    status: mc.status || (mc.is_active === false ? 'Inactive' : 'Active'),
                    delivery_challans: deliveryChallans,
                    delivery_challan_count: deliveryChallanCount,
                    delivery_challan_label: deliveryChallanLabel,

                    moisture_msand: mc.moisture?.msand ?? "",
                    moisture_csand: mc.moisture?.csand ?? "",
                    moisture_rsand: mc.moisture?.rsand ?? "",
                    moisture_12mm: mc.moisture?.["12mm"] ?? "",
                    moisture_20mm: mc.moisture?.["20mm"] ?? "",

                    absorption_msand: mc.absorption?.msand ?? "",
                    absorption_csand: mc.absorption?.csand ?? "",
                    absorption_rsand: mc.absorption?.rsand ?? "",
                    absorption_12mm: mc.absorption?.["12mm"] ?? "",
                    absorption_20mm: mc.absorption?.["20mm"] ?? "",
                };
                });

                setData({
                    total_pages: moistureList.total_pages ?? 0,
                    records: tableData,
                });
            })
            .catch((error) => {
                setData({ total_pages: 0, records: [] });
                displayError(error?.response?.data ?? error.message, 'Failed to load moisture correction list');
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [cookies, refreshKey]);

    return (

        <div id="outer-container" className="App">
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <div id="page-wrap">
                <ListFormPageHeader module="QUALITY CONTROL" title="List of Moisture Correction" formLink="/MoistureCorrection" addNewLabel="New Moisture Correction" />
                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title='List of Moisture Correction'
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditMoisturecorr}
                            onRowDelete={onDeleteMoisturecorr}
                            onRowPrint={onPrintMoistureCorrection}
                            hidePrint={false}
                            actionColumnField="id"
                            hideDelete={true}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                        />
                    </div>
                </div>
            </div>

            <button
                style={{ position: "relative", right: "40px" }}
                className="btn btn-fill btn-primary float-end"
                type="button"
                onClick={() => navigate("/MoistureCorrection")}
            >
                Back
            </button>

        </div>

    );
}

export default MoistureCorrectionTable;
